import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useBoardId, useListId, useWorkspaceId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { AddIcon } from '@trello/nachos/icons/add';
import type { ListTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { openCardComposer } from 'app/src/components/CardComposer';
import { readListVisibleCardsFromCache } from './readListVisibleCardsFromCache';

import styles from './ListAddCardButton.less';

export const ListAddCardButton: FunctionComponent = () => {
  const boardId = useBoardId();
  const listId = useListId();
  const workspaceId = useWorkspaceId();

  const onClick = useCallback(() => {
    const cards = readListVisibleCardsFromCache({ boardId, listId });
    const lastCard = cards.pop();
    openCardComposer({
      listId,
      position: lastCard?.pos ?? 0,
    });
    Analytics.sendClickedButtonEvent({
      buttonName: 'addAnotherCardButton',
      source: 'listFooter',
      containers: formatContainers({
        idBoard: boardId,
        idList: listId,
        idOrganization: workspaceId,
      }),
    });
  }, [boardId, listId, workspaceId]);

  return (
    <Button
      appearance="subtle"
      size="fullwidth"
      className={styles.listAddCardButton}
      onClick={onClick}
      iconBefore={<AddIcon color="currentColor" size="small" />}
      data-testid={getTestId<ListTestIds>('list-add-card-button')}
    >
      <FormattedMessage
        id="templates.list.add-a-card"
        defaultMessage="Add a card"
        description="Button text to add a card to a list"
      />
    </Button>
  );
};
